import React, { FC, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import triggerTracking from '@blueprint/tracking';

import {
    isTravelTimeSearchSelector,
    filtersValuesSelector,
    locationSelector,
    identifierSelector,
    locationTextSelector
} from '../../../reducers/filters';

import { selectCurrentSortOrder } from '../../../reducers/listingsSortOrder';
import {
    BedroomsWrapper,
    customThemeOverrides,
    SearchInitationWrapper,
    PriceRangeMultiColumnDropdownWrapper,
    PropertyTypeWrapper,
    SearchRadiusWrapper,
    StyledButtonWrapper,
    StyledButton,
    TravelTimeWrapper,
    StyledDivider,
    StyledDividerInner,
    LocationWrapper,
    HousePricesLocationWrapper
} from './SearchInitiation.styles';
import { SelectPriceForSale } from './SelectPriceForSale';
import { SelectPriceToRent } from './SelectPriceToRent';
import { SelectRadius } from './SelectRadius';
import { SelectBeds } from './SelectBeds';
import { SelectPropertyType } from './SelectPropertyType';
import { SelectMaxTravelTimeAndTransportMode } from './SelectMaxTravelTimeAndTransportMode';
import {
    SearchContext,
    SearchPageType,
    SearchSource
} from '../../../search/SearchContext';
import { LocationSearchInput } from '../LocationSearchInput/LocationSearchInput';
import { Tab } from '../../../homepage/components/HeroContainer/HeroContainer.types';
import { ThemeProvider } from '@blueprint/ui';
import { searchHousePrices, searchProperties } from 'search/helpers';

const SEARCH_BUTTON_TEXT = 'Search';

type SearchInitiationProps = {
    searchType: Tab;
    showRadius?: boolean;
    eventCategory: 'homepage' | 'search results';
    searchSource: SearchSource;
    excludePageSize?: boolean;
};

export const SearchInitiation: FC<SearchInitiationProps> = ({
    searchType,
    showRadius = true,
    eventCategory,
    searchSource,
    excludePageSize = false
}) => {
    const router = useRouter();

    const { state, dispatch } = useContext(SearchContext);
    const filters = useSelector(filtersValuesSelector);
    const [
        hasSubmittedLocationSearch,
        setHasSubmittedLocationSearch
    ] = useState(false);
    const { selectedValue: locationValue } = useSelector(locationSelector);
    const { selectedValue: identifier } = useSelector(identifierSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);
    const { selectedValue: locationTextValue } = useSelector(
        locationTextSelector
    );
    const currentSortOrder = useSelector(selectCurrentSortOrder);

    const { userAlertId } = state;
    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const isHousePricesSearch = searchType === 'house-prices';

    const value = locationTextValue || locationValue;

    const handleSearchProperties = () => {
        const eventLabel = !!value
            ? `${searchType}-success`
            : `${searchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const excludeRadius = !showRadius;

        if (!value) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        searchProperties(
            userAlertId,
            router,
            dispatch,
            searchType as SearchPageType,
            searchSource,
            filters,
            currentSortOrder,
            excludeRadius,
            excludePageSize
        );
    };

    const handleSearchHousePrices = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            // No failure label as the button is disabled if there is no content
            eventLabel: 'house-prices-success'
        });

        searchHousePrices(value, identifier);
    };

    const renderRadiusAndTravelTime = () => {
        if (!isTravelTimeSearch) {
            if (showRadius) {
                return (
                    <>
                        <StyledDivider>
                            <StyledDividerInner />
                        </StyledDivider>
                        <SearchRadiusWrapper>
                            <SelectRadius />
                        </SearchRadiusWrapper>
                    </>
                );
            }

            return null;
        }

        return (
            <ThemeProvider theme={customThemeOverrides}>
                <TravelTimeWrapper>
                    <SelectMaxTravelTimeAndTransportMode />
                </TravelTimeWrapper>
            </ThemeProvider>
        );
    };

    if (isHousePricesSearch) {
        return (
            <ThemeProvider theme={customThemeOverrides}>
                <SearchInitationWrapper>
                    <HousePricesLocationWrapper>
                        <LocationSearchInput
                            id="header-location"
                            onLocationInputKeyDown={handleSearchHousePrices}
                            hasExtendedHeight
                            hasDarkPurpleBorder
                            hasBorderRight={false}
                            searchType="house-prices"
                            value={locationValue}
                        />
                    </HousePricesLocationWrapper>
                    <StyledButtonWrapper>
                        <StyledButton
                            type="button"
                            onClick={handleSearchHousePrices}
                            data-testid="house-prices-search-button"
                            disabled={!value}
                        >
                            {SEARCH_BUTTON_TEXT}
                        </StyledButton>
                    </StyledButtonWrapper>
                </SearchInitationWrapper>
            </ThemeProvider>
        );
    }

    const showError = hasSubmittedLocationSearch && !value;

    return (
        <ThemeProvider theme={customThemeOverrides}>
            <SearchInitationWrapper>
                <LocationWrapper>
                    <LocationSearchInput
                        id="header-location"
                        onLocationInputKeyDown={handleSearchProperties}
                        hasExtendedHeight
                        hasDarkPurpleBorder
                        hasBorderRight={false}
                        value={locationValue}
                        showError={showError}
                    />
                </LocationWrapper>
                {renderRadiusAndTravelTime()}

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <BedroomsWrapper>
                    <SelectBeds />
                </BedroomsWrapper>

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <PriceRangeMultiColumnDropdownWrapper>
                    {isForSaleSearch ? (
                        <SelectPriceForSale />
                    ) : (
                        <SelectPriceToRent />
                    )}
                </PriceRangeMultiColumnDropdownWrapper>

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <PropertyTypeWrapper>
                    <SelectPropertyType />
                </PropertyTypeWrapper>
                <StyledButtonWrapper>
                    <StyledButton
                        type="button"
                        onClick={handleSearchProperties}
                        data-testid="search-button"
                    >
                        {SEARCH_BUTTON_TEXT}
                    </StyledButton>
                </StyledButtonWrapper>
            </SearchInitationWrapper>
        </ThemeProvider>
    );
};
